import { useState, useEffect } from 'react'
import { backendApiClient } from '../lib/api'
import type { Song } from '../lib/api'
import { SongForm } from './SongForm'
import SearchResults from './SearchResults'
import type { SearchFilters, SearchResult } from '../utils/searchUtils'
import { filterSongs } from '../utils/searchUtils'
import { useAuth } from '../contexts/AuthContext'
import { UserDropdownMenu } from './layout/UserDropdownMenu'

interface SongListProps {
  onEditSong: (songId: string) => void
}

export function SongList({ onEditSong }: SongListProps) {
  const { user: _user } = useAuth()
  const [songsData, setSongsData] = useState<Song[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [currentSearch, setCurrentSearch] = useState<SearchFilters>({ query: '', status: 'all', tags: [], sortBy: 'updated_at', sortOrder: 'desc' })
  const [isSearching, setIsSearching] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')

  // Load songs from API
  useEffect(() => {
    const loadSongs = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await backendApiClient.listSongs()
        // Handle response format - extract songs array if wrapped
        const songs = Array.isArray(response) ? response :
                      Array.isArray(response?.songs) ? response.songs : []
        setSongsData(songs)
      } catch (err) {
        // Ignore abort errors that occur when the page unmounts during navigation
        if ((err as Error)?.name === 'AbortError') {
          return
        }
        setError(err instanceof Error ? err.message : 'Failed to load songs')
        console.error('Failed to load songs:', err)
      } finally {
        setLoading(false)
      }
    }

    loadSongs()
  }, [])

  const songs = songsData || []

  // Separate effect to update search results when songs or search criteria change
  useEffect(() => {
    const songsArray = songsData || []
    if (songsArray.length > 0) {
      const results = filterSongs(songsArray, currentSearch)
      setSearchResults(results)
    }
  }, [songsData, currentSearch])

  const handleSongCreated = () => {
    setShowCreateForm(false)
    // Refresh songs list after creation
    const loadSongs = async () => {
      try {
        const response = await backendApiClient.listSongs()
        const songs = Array.isArray(response) ? response :
                      Array.isArray(response?.songs) ? response.songs : []
        setSongsData(songs)
      } catch (err) {
        console.error('Failed to reload songs after creation:', err)
      }
    }
    loadSongs()
  }

  // Handle search
  const handleSearch = (filters: SearchFilters) => {
    setIsSearching(true)
    setCurrentSearch(filters)

    // Filter songs based on search criteria
    const results = filterSongs(songs, filters)
    setSearchResults(results)

    setTimeout(() => setIsSearching(false), 300) // Small delay for smooth UX
  }

  const handleDeleteSong = async (songId: string) => {
    if (!confirm('Are you sure you want to delete this song?')) {
      return
    }

    try {
      await backendApiClient.deleteSong(songId)
      // Remove the song from local state
      setSongsData(prev => prev ? prev.filter(song => song.id !== songId) : null)
    } catch (err) {
      console.error('Failed to delete song:', err)
    }
  }

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusStyle = () => {
      switch (status) {
        case 'completed':
          return 'bg-green-100 text-green-800'
        case 'in_progress':
          return 'bg-yellow-100 text-yellow-800'
        case 'draft':
        default:
          return 'bg-gray-100 text-gray-800'
      }
    }

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide ${getStatusStyle()}`}>
        {status.replace('_', ' ')}
      </span>
    )
  }

  // V4 Song Card component
  const V4SongCard = ({ song }: { song: Song }) => (
    <div
      className="bg-white border border-gray-200 rounded-lg p-6 transition-all duration-200 hover:border-blue-500 hover:shadow-lg hover:-translate-y-1 cursor-pointer"
      onClick={() => onEditSong(song.id)}
    >
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{song.title}</h3>
        <StatusBadge status={song.status || 'draft'} />
      </div>
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
        {song.settings?.central_theme || 'No theme set...'}
      </p>
      <div className="text-xs text-gray-500 mb-4">
        <div>Created: {song.created_at ? new Date(song.created_at).toLocaleDateString() : 'Unknown'}</div>
        <div>Modified: {song.updated_at ? new Date(song.updated_at).toLocaleDateString() : 'Unknown'}</div>
      </div>
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {song.lyrics?.split('\n').filter(line => line.trim()).length || 0} lines
        </div>
        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
          {song.status === 'completed' ? 'View Song' : 'Continue Writing'}
        </button>
      </div>
    </div>
  )

  // V4 Song Row component for list view
  const V4SongRow = ({ song }: { song: Song }) => (
    <div
      className="bg-white border border-gray-200 rounded-lg p-6 transition-all duration-200 hover:border-blue-500 hover:shadow-md cursor-pointer"
      onClick={() => onEditSong(song.id)}
      data-testid="song-row"
    >
      <div className="grid grid-cols-12 gap-4 items-center">
        <div className="col-span-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="song-row-title">{song.title}</h3>
          <p className="text-sm text-gray-600 line-clamp-1">{song.settings?.central_theme || 'No theme set...'}</p>
        </div>
        <div className="col-span-2">
          <StatusBadge status={song.status || 'draft'} />
        </div>
        <div className="col-span-2">
          <div className="text-sm text-gray-600">
            <div>{song.lyrics?.split('\n').filter(line => line.trim()).length || 0} lines</div>
          </div>
        </div>
        <div className="col-span-2">
          <div className="text-sm text-gray-600">
            <div>{song.updated_at ? new Date(song.updated_at).toLocaleDateString() : 'Unknown'}</div>
          </div>
        </div>
        <div className="col-span-1 text-right">
          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-testid="song-row-edit-button">
            Edit
          </button>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--auth-bg-primary)' }}>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500 mx-auto"></div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading your songs...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--auth-bg-primary)' }}>
      {/* Header Section */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src="/assets/versifai_logo.svg"
                alt="Versifai"
                className="h-10 w-auto"
              />
            </div>
            <UserDropdownMenu mode="compact" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar with Stats and Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Stats Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Progress</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Total Songs</span>
                    <span className="font-medium">{songs.length}</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg h-2 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-lg transition-all duration-300" style={{ width: `${Math.min(100, (songs.length / 25) * 100)}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Completed</span>
                    <span className="font-medium">{songs.filter(s => s.status === 'completed').length}</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg h-2 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-lg transition-all duration-300" style={{ width: songs.length > 0 ? `${(songs.filter(s => s.status === 'completed').length / songs.length) * 100}%` : '0%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">In Progress</span>
                    <span className="font-medium">{songs.filter(s => s.status === 'in_progress').length}</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg h-2 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-lg transition-all duration-300" style={{ width: songs.length > 0 ? `${(songs.filter(s => s.status === 'in_progress').length / songs.length) * 100}%` : '0%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Drafts</span>
                    <span className="font-medium">{songs.filter(s => s.status === 'draft').length}</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg h-2 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-lg transition-all duration-300" style={{ width: songs.length > 0 ? `${(songs.filter(s => s.status === 'draft').length / songs.length) * 100}%` : '0%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    <div>
                      <div className="font-medium text-gray-900">New Song</div>
                      <div className="text-sm text-gray-600">Start fresh</div>
                    </div>
                  </div>
                </button>
                <button className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                    </svg>
                    <div>
                      <div className="font-medium text-gray-900">Import</div>
                      <div className="text-sm text-gray-600">Upload lyrics</div>
                    </div>
                  </div>
                </button>
                <button className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                    </svg>
                    <div>
                      <div className="font-medium text-gray-900">Templates</div>
                      <div className="text-sm text-gray-600">Song structures</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {/* Welcome Section */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 data-testid="your-songs-title" className="text-3xl font-bold text-gray-900 mb-2">Your Songs</h2>
                  <p className="text-gray-600">Create, edit, and organize your lyrical masterpieces</p>
                </div>
                <div className="mt-4 sm:mt-0">
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg inline-flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    Create New Song
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-8 rounded-lg bg-red-50 border border-red-200 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-4">
                    <span className="text-red-500 text-lg">⚠️</span>
                  </div>
                  <div className="text-red-800 font-medium">{error}</div>
                </div>
              </div>
            )}

            {/* Create Form */}
            {showCreateForm && (
              <div className="mb-12">
                <div className="bg-white border border-gray-200 rounded-lg p-8">
                  <div className="flex justify-between items-start mb-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">Create New Song</h2>
                      <p className="text-gray-600 mt-2 text-lg">Start your next musical masterpiece</p>
                    </div>
                    <button
                      onClick={() => setShowCreateForm(false)}
                      className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 rounded-lg transition-all duration-200 hover:scale-105"
                    >
                      <span className="text-xl">✕</span>
                    </button>
                  </div>
                  <SongForm key="create" onSuccess={handleSongCreated} onCancel={() => setShowCreateForm(false)} />
                </div>
              </div>
            )}

            {/* Search, Filters, and View Toggle */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search your songs..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-white transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-0 focus:shadow-lg text-gray-900 placeholder-gray-500"
                      onChange={(e) => handleSearch({ ...currentSearch, query: e.target.value })}
                    />
                    <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <select
                    className="px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-900"
                    onChange={(e) => handleSearch({ ...currentSearch, status: e.target.value as Song['status'] | 'all' })}
                  >
                    <option value="all">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                  <div className="inline-flex bg-white border border-gray-200 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'grid' ? 'bg-blue-500 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'list' ? 'bg-blue-500 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Songs Display */}
            <div>
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {currentSearch.query ? 'Search Results' : 'Your Songs'}
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {currentSearch.query ? (
                        `${searchResults.length} ${searchResults.length === 1 ? 'song' : 'songs'} found`
                      ) : (
                        `${songs.length} ${songs.length === 1 ? 'song' : 'songs'} in your creative library`
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Display search results or regular song list */}
              {currentSearch.query || currentSearch.status !== 'all' || (currentSearch.tags && currentSearch.tags.length > 0) ? (
                <SearchResults
                  results={searchResults}
                  query={currentSearch.query}
                  onEditSong={onEditSong}
                  onDeleteSong={handleDeleteSong}
                  loading={isSearching}
                />
              ) : songs.length === 0 ? (
                <div className="text-center py-20">
                  <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-16">
                    <div className="text-6xl mb-4 opacity-70">🎵</div>
                    <div className="max-w-md mx-auto">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Your creative journey starts here</h3>
                      <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                        Every legendary song began as an idea. Let's turn your inspiration into music.
                      </p>
                      <button
                        onClick={() => setShowCreateForm(true)}
                        data-testid="create-first-song-button"
                        className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center space-x-3"
                      >
                        <span className="text-xl">+</span>
                        <span>Create Your First Song</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="song-grid">
                      {songs.map((song, index) => (
                        <div key={song.id} className="opacity-0 animate-in" style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}>
                          <V4SongCard song={song} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="song-list">
                      {songs.map((song, index) => (
                        <div key={song.id} className="opacity-0 animate-in" style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}>
                          <V4SongRow song={song} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Load More */}
                  {songs.length > 10 && (
                    <div className="mt-8 text-center">
                      <button className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                        Load More Songs
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
